import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type ReactNode,
  type ForwardedRef,
} from "react";

import { type Conversation } from "shared/apiClient";
import { type Noticeboard } from "shared/lib";
import { MessageType } from "shared/types";

import { useCreateConversationMessage } from "shared/apiHooks";
import { useProjectDetails } from "shared/hooks";
import { CaptureEventType, useCaptureEvent } from "@/hooks";

import EditProposalDialog from "./EditProposalDialog";

export interface EditProposalDialogContainerRef {
  openDialog: () => void;
}

export interface EditProposalDialogContainerProps {
  children?: (openDialog: () => void) => ReactNode;
  conversation: Conversation | undefined | null;
  noticeboardData: Noticeboard | undefined | null;
}

export const EditProposalDialogContainer = forwardRef(
  function EditProposalDialogContainer(
    {
      children,
      conversation,
      noticeboardData,
    }: EditProposalDialogContainerProps,
    ref: ForwardedRef<EditProposalDialogContainerRef>,
  ) {
    const { captureEvent } = useCaptureEvent();

    const [editProposalDialogOpen, setEditProposalDialogOpen] = useState(false);

    const { content: projectDetails } = useProjectDetails(
      conversation,
      noticeboardData,
      "enquiry",
    );

    const filteredProjectDetails = useMemo(() => {
      return projectDetails?.replace(/^<.*>/, "").replace(/^<\/.*>/, "");
    }, [projectDetails]);

    useEffect(() => {
      if (editProposalDialogOpen) {
        captureEvent(CaptureEventType.USER_ADJUST_PROPOSAL_RENDERED);
      }
    }, [editProposalDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const {
      mutate: createMessage,
      isError: isCreateMessageError,
      isPending: isCreateMessagePending,
    } = useCreateConversationMessage({
      onError: (error) => {
        console.error(
          "<EditProposalDialogContainer> useCreateConversationMessage() onError()",
          error,
        );
        setEditProposalDialogOpen(false);
      },
      onSuccess: () => {
        captureEvent(CaptureEventType.USER_ADJUSTED_PROPOSAL);
        setEditProposalDialogOpen(false);
      },
    });

    const handleSubmitEditProposal = (text: string) => {
      createMessage({
        body: {
          text,
          message_type: MessageType.AMENDMENT,
        },
        path: {
          application_id: conversation?.application || "",
          conversation_id: conversation?.id || "",
        },
      });
    };

    const openDialog = useCallback(() => {
      setEditProposalDialogOpen(true);
    }, []);

    useImperativeHandle(ref, () => {
      return { openDialog };
    }, [openDialog]);

    return (
      <>
        {children?.(openDialog)}

        <EditProposalDialog
          content={filteredProjectDetails}
          onOpenChange={setEditProposalDialogOpen}
          onSubmit={handleSubmitEditProposal}
          open={editProposalDialogOpen}
          submitting={isCreateMessagePending}
        />
      </>
    );
  },
);
